import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Chip } from "primereact/chip";
import { useDispatch, useSelector } from "react-redux";
import CustomDialog from "../../../components/CustomDialog";
import { CustomButton } from "../../../components/CustomButton";
import Loader from "../../../components/Loader";
import SklbtConfirmation from "../../../components/common/SklbtConfirmation";
import { Toast } from "primereact/toast";
import "./ManageTag.css";
import "./tagStyle.css";

import {
  fetchAllTags,
  createTag,
  updateTag,
  deleteTag,
} from "../../../reducers/tagslices";

const COLORS = [
  "#93C5FD",
  "#86EFAC",
  "#FCA5A5",
  "#FCD34D",
  "#C4B5FD",
  "#67E8F9",
  "#FDE68A",
  "#F9A8D4",
  "#D1D5DB",
  "#99F6E4",
];

const ManageTag = ({ visible, onHide }) => {
  const dispatch = useDispatch();
  const { tags = [], loading } = useSelector((state) => state.tag);
  const toast = useRef(null);
  const withFullOpacity = (color) =>
    typeof color === "string" && color.length === 9
      ? `${color.slice(0, 7)}4D`
      : `${color}4D`;

  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(COLORS[0]);
  const [editingTag, setEditingTag] = useState(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Fetch tags on modal open
  useEffect(() => {
    if (visible) dispatch(fetchAllTags());
  }, [dispatch, visible]);

  const resetForm = () => {
    setTagName("");
    setTagColor(COLORS[0]);
    setEditingTag(null);
  };

  const getToastMessage = (payload, fallback) => {
    if (typeof payload === "string" && payload.trim()) return payload;
    if (typeof payload?.data === "string" && payload.data.trim()) {
      return payload.data;
    }
    if (typeof payload?.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    return fallback;
  };

  const handleSubmit = async () => {
    const name = tagName.trim();
    if (!name) {
      toast.current?.show({
        severity: "warn",
        detail: "Tag name is required",
      });
      return;
    }
    try {
      if (editingTag) {
        const res = await dispatch(
          updateTag({ tagId: editingTag.id, formData: { tagName: name, tagColor } })
        ).unwrap();
        toast.current?.show({
          severity: "success",
          detail: getToastMessage(res, "Tag updated"),
        });
      } else {
        const res = await dispatch(createTag({ tagName: name, tagColor })).unwrap();
        toast.current?.show({
          severity: "success",
          detail: getToastMessage(res, "Tag created"),
        });
      }
      resetForm();
      dispatch(fetchAllTags()); // refresh list
    } catch (err) {
      const errorMessage =
        getToastMessage(err, "") ||
        getToastMessage(err?.response?.data, "") ||
        "Tag save failed";
      toast.current?.show({
        severity: "error",
        detail: errorMessage,
      });
      console.error("Tag save failed:", errorMessage || err);
    }
  };

  const handleEdit = (row) => {
    setEditingTag(row);
    setTagName(row.tagName);
    setTagColor(row.tagColor);
  };

  const handleDelete = async (tagId) => {
    if (!tagId) return;
    try {
      await dispatch(deleteTag(tagId)).unwrap();
      if (editingTag?.id === tagId) resetForm();
      dispatch(fetchAllTags()); // refresh list
      toast.current?.show({
        severity: "success",
        detail: "Tag deleted",
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        detail: err?.message || "Tag delete failed",
      });
      console.error("Tag delete failed:", err?.message || err);
    }
  };

  const openDeleteConfirm = (row) => {
    setTagToDelete(row);
    setDeleteConfirmVisible(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmVisible(false);
    setTagToDelete(null);
  };

  const confirmDelete = () => {
    if (!tagToDelete?.id) return;
    handleDelete(tagToDelete.id);
    closeDeleteConfirm();
  };

  return (
    <CustomDialog
      header="Manage Tags"
      visible={visible}
      onHide={() => {
        resetForm();
        onHide();
      }}
      className="manage-tag-dialog md-dialog"
    >
      <Toast ref={toast} />
      <SklbtConfirmation
        visible={deleteConfirmVisible}
        handleConfirm={confirmDelete}
        handleReject={closeDeleteConfirm}
        message={`Deleting tag "${tagToDelete?.tagName || "this tag"}" will remove it from all assigned customers. Do you want to continue?`}
        header="Delete Tag"
      />
      <Loader visible={loading} />
      {/* ===== FORM ===== */}
      <div className="manage-tag-section p-2 mb-3">
        <div className="flex flex-column gap-2">
          <label className="manage-tag-label text-xs font-semibold">
            Tag Name
          </label>
          <InputText
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            placeholder="e.g. Important"
            className="p-inputtext-sm"
          />

          <label className="manage-tag-label text-xs font-semibold mt-1">
            Tag Color
          </label>
          <div className="flex gap-2 flex-wrap">
            {COLORS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setTagColor(color)}
                style={{
                  backgroundColor: color,
                  width: 30,
                  height: 30,
                  borderRadius: 0,
                }}
                className={`manage-tag-color-swatch ${
                  tagColor === color ? "is-selected" : ""
                }`}
              />
            ))}
          </div>

          <div className="flex justify-content-end gap-2 mt-2">
            {editingTag && (
              <CustomButton
                label="Cancel"
                varient="outline"
                onClick={resetForm}
                disabled={loading}
              />
            )}
            <CustomButton
              label="Save"
              icon="pi pi-save"
              onClick={handleSubmit}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <DataTable
        value={tags}
        loading={loading}
        stripedRows
        size="small"
        className="manage-tag-table text-xs"
        tableStyle={{ textAlign: "center" }}
      >
        <Column
          header="Tag Name"
          body={(row) => (
            <Chip
              label={`#${row.tagName}`}
              style={{
                backgroundColor: withFullOpacity(row.tagColor),
                color: "#111",
                fontSize: "0.65rem",
              }}
              className="tag-chip px-2 py-0.5 leading-none relative"
            />
          )}
        />

        <Column
          header="Color"
          body={(row) => (
            <div className="flex justify-content-center">
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 0,
                  backgroundColor: row.tagColor,
                }}
              />
            </div>
          )}
        />

        <Column
          header="Status"
          body={(row) => (
            <span
              className={`px-2 py-1 border-round-md text-xs font-semibold ${
                row.isActive
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {row.isActive ? "Active" : "Inactive"}
            </span>
          )}
        />

        <Column
          header="Action"
          body={(row) => (
            <div className="flex justify-content-center gap-1">
              <CustomButton
                icon="pi pi-pencil"
                varient="icon-button"
                onClick={() => handleEdit(row)}
                disabled={loading}
              />
              <CustomButton
                icon="pi pi-trash"
                varient="icon-button"
                onClick={() => openDeleteConfirm(row)}
                style={{ color: "#DC2626" }}
                disabled={loading}
              />
            </div>
          )}
          style={{ width: "6rem" }}
        />
      </DataTable>
    </CustomDialog>
  );
};

export default ManageTag;
